import { assert } from "@lib/utils/utils";
import Matrix from "./matrix";
import { unilateralAlloc } from "./unilateralAlloc";
import Vector from "./vector";

export function accountsOfGuarantees(
  guarantees: Matrix<"evaluator", "evaluable+hold">, // (evaluators may include hold)
  responsibilityProportions: Vector<"evaluator">,
): Matrix<"evaluator", "evaluable+hold"> {
  const numEvaluables = guarantees.shape[1];
  const result = guarantees.zerosLike();

  for (let colNo = 0; colNo < numEvaluables; colNo++) {
    let prevAmount = 0;
    const col = guarantees.getCol(colNo);
    const uniqueAmounts = col.unique();
    for (const amount of uniqueAmounts) {
      const contributors = col
        .map((c, i) => (c >= amount ? i : -1))
        .filter((n) => n >= 0);
      const sharedAmount = amount - prevAmount;
      const rowResponsibilityProportions = responsibilityProportions
        .filter((_, i) => contributors.includes(i))
        .normalize();
      const perContributorAmount = rowResponsibilityProportions.map(
        (proportion) => sharedAmount * proportion,
      );
      contributors.forEach((contributorNo, amountNo) => {
        result.set(
          contributorNo,
          colNo,
          result.get(contributorNo, colNo) + perContributorAmount.get(amountNo),
        );
      });
      prevAmount = amount;
    }
  }

  return result;
}

/** Implement the Algorithm described in [On the S-Process](https://www.overleaf.com/project/65fa09a01060a6a884ab48e2).
 *
 * @param evaluatorBudgets - The budgets of each evaluator.
 * @param trancheTables - Tranche tables for each evaluator. This is a list of
 * tranche tables (one per evaluator). Each of those an n_tranch x n_evaluables
 * matrix. These should be *cumulative* tranche tables (not marginal).
 * @returns The final accounts and guarantees. These are both n_evaluators x
 * n_evaluables matrices.
 */
export function singleLayerSProcess(
  evaluatorBudgets: Vector<"evaluator">, // (evaluators may include hold)
  trancheTables: Matrix<"tranche", "evaluable+hold">[],
): {
  accounts: Matrix<"evaluator", "evaluable+hold">;
  guarantees: Matrix<"evaluator", "evaluable+hold">;
  epochs: number;
} {
  const numEvaluators = trancheTables.length;
  const numEvaluables = trancheTables[0].shape[1];
  const responsibilityProportions = evaluatorBudgets.normalize();

  let remainingBudget = evaluatorBudgets;
  let accounts = Matrix.zeros<"evaluator", "evaluable+hold">(
    numEvaluators,
    numEvaluables,
  );
  let guarantees = Matrix.zeros<"evaluator", "evaluable+hold">(
    numEvaluators,
    numEvaluables,
  );
  // let previousRemainingBudget = remainingBudget;
  let budgetAdjustment = Vector.zeros<"evaluator">(numEvaluators);

  let epochs = 0;
  // TODO(SP-487): make convergence threshold 1 again.
  while (remainingBudget.some((b) => b > 50)) {
    epochs++;

    if (epochs > 100) {
      throw new Error(
        `Algorithm did not converge in ${epochs} epochs. (remainingBudget: [${remainingBudget.toArray().join(", ")}])`,
      );
    }

    const accountGuaranteeDiff = Vector.zeros<"evaluator">(numEvaluators).map(
      (_, evaluatorIndex) => {
        const account = accounts.getRow(evaluatorIndex);
        const guarantee = guarantees.getRow(evaluatorIndex);
        return guarantee
          .map((g: number, i: number) => g - account.get(i))
          .sum();
      },
    );

    const newBudgets = evaluatorBudgets
      .add(accountGuaranteeDiff)
      .add(budgetAdjustment);
    guarantees = Matrix.ofRows(
      trancheTables.map((trancheTable, i) =>
        unilateralAlloc(trancheTable, newBudgets.get(i)),
      ),
    );

    accounts = accountsOfGuarantees(guarantees, responsibilityProportions);

    remainingBudget = evaluatorBudgets.map(
      (budget, i) => budget - accounts.getRow(i).sum(),
    );
    // previousRemainingBudget = remainingBudget;
    // const budgetRemainingDiff = previousRemainingBudget.map(
    //   (b, i) => b - remainingBudget.get(i),
    // );
    // const slope = budgetRemainingDiff.map((diff, i) =>
    //   Math.abs(remainingBudget.get(i)) > 1 ? diff / remainingBudget.get(i) : 0,
    // );

    // for (let i = 0; i < numEvaluators; i++) {
    //   const absSlope = Math.abs(slope.get(i));
    //   if (0 < absSlope && absSlope < 0.5) {
    //     budgetAdjustment.set(
    //       i,
    //       budgetAdjustment.get(i) + budgetRemainingDiff.get(i) / slope.get(i),
    //     );
    //   }
    // }
  }

  assert(
    accounts.n_rows === numEvaluators,
    "accounts.n_rows should be equal to numEvaluators",
  );
  assert(
    guarantees.n_rows === numEvaluators,
    "guarantees.n_rows should be equal to numEvaluators",
  );
  assert(
    accounts.n_cols === numEvaluables,
    "accounts.n_cols should be equal to numEvaluables",
  );
  assert(
    guarantees.n_cols === numEvaluables,
    "guarantees.n_cols should be equal to numEvaluables",
  );
  for (let i = 0; i < accounts.n_rows; i++) {
    assert(
      remainingBudget.get(i) >= -2,
      "remainingBudget should be non-negative",
    );
    const accountsSum = accounts.getRow(i).sum();
    const guaranteesSum = guarantees.getRow(i).sum();
    assert(
      accountsSum <= guaranteesSum,
      "accountsSum should be less than or equal to guaranteesSum",
    );
    assert(
      accountsSum <= evaluatorBudgets.get(i) + 1,
      "accountsSum should be less than or equal to evaluatorBudgets",
    );
  }
  return { accounts, guarantees, epochs };
}
